/** Angular */
import { Injectable } from '@angular/core';
/** Rxjs */
import { Observable } from 'rxjs';
/** Services */
import { BydRhsAbstractService } from '../../../shared/services/byd-rhs-abstract.service';
/** Sixense */
import { BydModel, BydHttpParams } from '@sixense/byd-core-service';
/** Models */
import { DsFormTemplateInterface } from '../interfaces/foms-models/ds-form-template.interface';
import { DsFormTemplate } from '../models/ds-form-template';

@Injectable({
  providedIn: 'root',
})
export class DsFormOrganisationTemplatesService extends BydRhsAbstractService {
  /**
   * Implement inherited abstract member 'path' from BydRhsAbstractService
   */
  public path: string;

  /**
   * Add a form template for an organisation.
   *
   * @param organisationId Chosen organisation id
   * @param formTemplate new form template object
   * @returns Observable<number>
   */
  public addFormTemplate(organisationId: number, formTemplate: DsFormTemplate): Observable<number> {
    this.path = `/v2/organisations/${organisationId}/forms`;
    return this.post<number>({ body: formTemplate });
  }

  /**
   * Edits a form template for an organisation.
   *
   * @param organisationId Chosen organisation id
   * @param templateId id of the form template
   * @param formTemplate Edited form template object
   * @returns Observable<boolean>
   */
  public editFormTemplate(organisationId: number, templateId: number, formTemplate: DsFormTemplate) {
    this.path = `/v2/organisations/${organisationId}/forms/${templateId}`;
    return this.put<boolean>({ body: formTemplate });
  }

  /**
   * Returns selected form template, including fields property.
   *
   * @param organisationId Chosen organisation id
   * @param templateId id of the form template
   * @returns Observable<DsFormTemplate>
   */
  public getFormTemplate(organisationId: number, templateId: number): Observable<DsFormTemplate> {
    this.path = `/v2/organisations/${organisationId}/forms/${templateId}`;
    return this.getModel<DsFormTemplate>();
  }

  /**
   * Deletes the chosen form template for an organisation.
   *
   * @param organisationId Chosen organisation id
   * @param templateId id of the form template
   * @param linear Whether to call the linear API
   * @returns Observable<boolean>
   */
  public deleteFormTemplate(organisationId: number, templateId: number, linear = false) {
    this.path = `/${linear ? `v1` : `v2`}/organisations/${organisationId}/forms/${templateId}`;
    return this.delete<boolean>();
  }

  /**
   * Returns the list of organisation form templates.
   *
   * @param organisationId Chosen organisation id
   * @param categoryId Chosen organisation category id
   * @param templateSearch Search filter for template name
   * @param elementsPerPage Number of maximum elements per page to return
   * @param pageIndex Page index to return
   * @returns Observable<BydModel<DsFormTemplate>>
   */
  public getFormTemplates(
    organisationId: number,
    categoryId?: number,
    templateSearch = '',
    elementsPerPage = 10,
    pageIndex = 0
  ): Observable<BydModel<DsFormTemplate>> {
    this.path = `/v2/organisations/${organisationId}/forms`;
    const headers = this.bydRhsHeadersService.setHeadersWithPagination(elementsPerPage, pageIndex);
    let params = new BydHttpParams();
    if (categoryId) {
      params = params.set('categoryId', categoryId.toString());
    }
    if (templateSearch) {
      params = params.set('name', templateSearch);
    }
    return this.getModels<DsFormTemplate>({ headers, params });
  }

  /**
   * Create DsFormTemplate instance.
   *
   * @param  jsonObject The flat json object.
   * @returns an `DsFormTemplate` instance.
   */
  public createNewInstance(jsonObject: DsFormTemplateInterface): DsFormTemplate {
    return new DsFormTemplate(jsonObject);
  }
}
